import { PayeeStoreState } from "@/employer/types";
import {
    FetchState,
    HsabPayee, HsabPayeeLastActive
} from "@/shared/types";
import { defineStore } from "pinia";
import { useNotifierStore } from "@/shared/store/notifier";
import * as payeeService from "@/shared/api/payeeService";
import * as storeHelper from "@/shared/storeHelper";
import { areEquivalent } from "@/shared/stringHelper";
import _ from "lodash";
import moment from "moment/moment";

export const initialState: PayeeStoreState = {
    payeesForUser: [],
    fetchState: FetchState.Uninitialized,
    createState: FetchState.Uninitialized,
    saveState: FetchState.Uninitialized,
    deleteState: FetchState.Uninitialized,
    lastActivePayees: [],
};

export const usePayeeStore = defineStore("payee", {
    state: () => (_.cloneDeep(initialState)),
    getters: {
        payeesLoaded: state => state.fetchState === FetchState.Loaded,
        isFetchingLoading: state => state.fetchState === FetchState.Loading,
        isCreateLoading: state => state.createState === FetchState.Loading,
        isSaveLoading: state => state.saveState === FetchState.Loading,
        isDeleteLoading: state => state.deleteState === FetchState.Loading,
        sortedLastActivePayees (): HsabPayee[] {
            return this.lastActivePayees.map(t => t.payee);
        },
    },
    actions: {
        loadLastActivePayeesFromStorage () {
          const payees = JSON.parse(localStorage.getItem("lastActivePayees"));
          if (payees) {
              this.lastActivePayees = payees;
          }
        },
        setPayeeActivityUpdate (payee: HsabPayee) {
            let payees = this.lastActivePayees;
            let payeeLastActive = payees.find(t => t.payee.payeeId === payee.payeeId);
            if (payeeLastActive) {
                payeeLastActive.lastActiveDate = moment();
            } else {
                payeeLastActive = { payee, lastActiveDate: moment() };
                payees.push(payeeLastActive);
            }
            payees = payees.sort((a, b) => moment(b.lastActiveDate).valueOf() - moment(a.lastActiveDate).valueOf());
            payees = payees.slice(0, 10);
            this.lastActivePayees = payees;
            localStorage.setItem("lastActivePayees", JSON.stringify(this.lastActivePayees));
        },
        deletePayeeActivityRecord (payeeId: number) {
            const payees = _.remove(this.lastActivePayees, (t: HsabPayeeLastActive) => t.payee.payeeId === payeeId);
            this.lastActivePayees = payees;
            localStorage.setItem("lastActivePayees", JSON.stringify(this.lastActivePayees));
        },
        loadPayees () {
            if (this.fetchState === FetchState.Loading) {
                return Promise.resolve();
            }
            if (this.fetchState === FetchState.Loaded) {
                return Promise.resolve(this.payeesForUser);
            }
            this.fetchState = FetchState.Loading;
            return payeeService.getPayees()
                .then(response => {
                    this.payeesForUser = response.data;
                    this.fetchState = FetchState.Loaded;
                }).catch(err => {
                    storeHelper.handleError({ error: err, rethrow: true });
                });
        },
        findPayee (payeeName: string): HsabPayee {
          if (this.payeesLoaded) {
              const found = this.payeesForUser.find(t => areEquivalent(payeeName, t.payeeName));
              if (found) {
                  return found;
              }
            }
            return null;
        },
        createPayee (p: HsabPayee) {
            if (this.createState === FetchState.Loading) {
                return;
            }

            this.createState = FetchState.Loading;
            return payeeService.createPayee(p)
                .then(response => {
                    this.payeesForUser.push(response.data);
                    this.createState = FetchState.Uninitialized;
                    return response.data;
                }).catch(err => {
                    storeHelper.handleError({ error: err, rethrow: true });
                });
        },
        savePayee (p: HsabPayee) {
            if (this.saveState === FetchState.Loading) {
                return;
            }

            this.saveState = FetchState.Loading;
            return payeeService.updatePayee(p)
                .then(response => {
                    _.remove(this.payeesForUser, (x: HsabPayee) => x.payeeId === p.payeeId);

                    this.payeesForUser.push(response.data);
                    this.saveState = FetchState.Uninitialized;
                    return response.data;
                }).catch(err => {
                    storeHelper.handleError({ error: err, rethrow: true });
                });
        },
        deletePayee (payeeId: number) {
            if (this.deleteState === FetchState.Loading) {
                return Promise.resolve();
            }

            this.deleteState = FetchState.Loading;
            const notifierStore = useNotifierStore();
            return payeeService.deletePayee(payeeId)
                .then(response => {
                    _.remove(this.payeesForUser, (x: HsabPayee) => x.payeeId === payeeId);
                    this.deleteState = FetchState.Uninitialized;
                    notifierStore.setSuccess("Payee has been deleted.");
                }).catch(err => {
                    this.deleteState = FetchState.Failed;
                    storeHelper.handleError({ error: err, rethrow: true });
                    notifierStore.setDanger("Error deleting payee, please try again.");
                });
        },
    }
});